// src/hooks/useFloatingMessaging.js - VERSION CORRIGÉE POUR GESTION DÉCONNEXION
// ✅ PHASE 5.1: Migration vers système pub/sub centralisé
import { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { get, post, put, del } from '../utils/apiClient'
import { useSocketContext, SOCKET_EVENTS } from '../contexts/SocketContext'

// ===== SINGLETON GLOBAL - RÉSISTANT À STRICTMODE =====
if (!window.__MESSAGING_SINGLETON__) {
  window.__MESSAGING_SINGLETON__ = {
    isDockOpen: false,
    openConversations: [],
    recentConversations: [],
    totalUnread: 0,
    loading: false,
    typingUsers: [],
    onlineUsers: new Set(),
    hasInitialized: false,
    listeners: new Set(),
    socket: null,
    isSocketConnected: false,
    instanceCount: 0,
    lastLoadTime: 0,
    loadPromise: null,
    processedMessageIds: new Set(),
    lastNotificationTime: 0,
    isInitializing: false,
    initializationPromise: null,
    currentUserId: null,
    isUserAuthenticated: false,
    // Passe à true dès qu'une déconnexion socket effective a eu lieu, pour
    // distinguer la connexion initiale (déjà couverte par le chargement au
    // boot) d'une reconnexion après perte réseau, qui elle exige un resync.
    hasLostConnection: false,
  }
}

const sharedState = window.__MESSAGING_SINGLETON__

if (!window.__CONVERSATION_LOCKS__) {
  window.__CONVERSATION_LOCKS__ = new Map()
}

// NOUVELLES FONCTIONS UTILITAIRES pour éviter la duplication
const findExistingConversationWithMember = (memberId) => {
  const currentUserId = getCurrentUserId()

  return sharedState.recentConversations.find((conv) => {
    if (conv.type !== 'private' || !conv.members || conv.members.length !== 2) {
      return false
    }

    const memberIds = conv.members.map((m) => Number(m.id))
    return memberIds.includes(Number(currentUserId)) && memberIds.includes(Number(memberId))
  })
}

const findOpenConversationWithMember = (memberId) => {
  const currentUserId = getCurrentUserId()

  return sharedState.openConversations.find((conv) => {
    if (conv.type !== 'private' || !conv.members || conv.members.length !== 2) {
      return false
    }

    const memberIds = conv.members.map((m) => Number(m.id))
    return memberIds.includes(Number(currentUserId)) && memberIds.includes(Number(memberId))
  })
}

const ensureRecentConversationsLoaded = async () => {
  if (
    sharedState.recentConversations.length === 0 ||
    Date.now() - sharedState.lastLoadTime > 30000
  ) {
    console.log('Rechargement des conversations pour vérification')
    await loadRecentConversationsInternal()
  }
}

const resetMessagingState = () => {
  console.log("🧹 RESET: Nettoyage complet de l'état de messagerie")

  sharedState.isDockOpen = false
  sharedState.openConversations = []
  sharedState.recentConversations = []
  sharedState.totalUnread = 0
  sharedState.loading = false
  sharedState.typingUsers = []
  sharedState.onlineUsers = new Set()
  sharedState.hasInitialized = false
  sharedState.isInitializing = false
  sharedState.initializationPromise = null
  sharedState.lastLoadTime = 0
  sharedState.loadPromise = null
  sharedState.processedMessageIds.clear()
  sharedState.lastNotificationTime = 0
  sharedState.currentUserId = null
  sharedState.isUserAuthenticated = false
  sharedState.hasLostConnection = false

  // Nettoyer le socket
  if (sharedState.socket) {
    try {
      sharedState.socket.disconnect()
    } catch (error) {
      console.error('Erreur lors de la déconnexion du socket:', error)
    }
    sharedState.socket = null
    sharedState.isSocketConnected = false
  }

  // Notifier tous les listeners du reset
  notifyListeners()
}

// ✅ SECURITY: Vérifier si l'utilisateur est toujours authentifié
// La vraie validation se fait côté serveur via le cookie httpOnly
const checkAuthenticationStatus = () => {
  const userId = localStorage.getItem('userId')

  if (!userId) {
    return false
  }

  // Note: L'expiration du cookie est gérée côté serveur
  // Le serveur retournera 401 si le cookie a expiré
  return true
}

// Fonction pour notifier avec protection anti-spam
const notifyListeners = () => {
  const now = Date.now()
  if (now - sharedState.lastNotificationTime < 50) {
    return
  }
  sharedState.lastNotificationTime = now

  console.log('📡 NOTIFICATION:', sharedState.listeners.size, 'listeners')
  sharedState.listeners.forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('❌ Erreur listener:', error)
    }
  })
}

// Fonction pour nettoyer les anciens messages traités
const cleanupProcessedMessages = () => {
  const MAX_SIZE = 500
  if (sharedState.processedMessageIds.size > MAX_SIZE) {
    // Convertir en tableau, garder les 500 derniers
    const recentIds = Array.from(sharedState.processedMessageIds).slice(-MAX_SIZE)
    sharedState.processedMessageIds = new Set(recentIds)
    console.log(`🧹 Nettoyage processedMessageIds: ${MAX_SIZE} messages gardés`)
  }
}

// NOUVELLE FONCTION : Surveiller les changements d'authentification
const setupAuthenticationWatcher = () => {
  // Écouter les événements de déconnexion
  const handleLogout = () => {
    console.log('🔓 Événement de déconnexion détecté')
    resetMessagingState()
  }

  // ✅ SECURITY: Écouter les changements de localStorage (déconnexion depuis un autre onglet)
  const handleStorageChange = (event) => {
    if (event.key === 'userId' && !event.newValue) {
      console.log('🔓 userId supprimé du localStorage')
      resetMessagingState()
    }
  }

  // Écouter les événements personnalisés de déconnexion
  document.addEventListener('user_logged_out', handleLogout)
  window.addEventListener('storage', handleStorageChange)

  // Vérification périodique de l'authentification
  const authCheckInterval = setInterval(() => {
    if (sharedState.isUserAuthenticated && !checkAuthenticationStatus()) {
      console.log('🔓 Vérification périodique: utilisateur non authentifié')
      resetMessagingState()
    }
  }, 30000) // Vérifier toutes les 30 secondes

  return () => {
    document.removeEventListener('user_logged_out', handleLogout)
    window.removeEventListener('storage', handleStorageChange)
    clearInterval(authCheckInterval)
  }
}

// Fonction pour vérifier si un message existe déjà
const messageExists = (conversationId, messageId) => {
  if (sharedState.processedMessageIds.has(messageId)) {
    return true
  }

  const openConv = sharedState.openConversations.find((c) => c.id === conversationId)
  if (openConv && openConv.messages) {
    const exists = openConv.messages.some((m) => m.id === messageId)
    if (exists) return true
  }

  const recentConv = sharedState.recentConversations.find((c) => c.id === conversationId)
  if (recentConv && recentConv.messages) {
    const exists = recentConv.messages.some((m) => m.id === messageId)
    if (exists) return true
  }

  return false
}

// ✅ PHASE 5 FIX: Stocker les références aux handlers pour cleanup ciblé
let floatingMessagingHandlers = null

// Configuration Socket.IO pour la messagerie
const setupSocketListeners = (socket) => {
  if (!socket) return

  // ✅ PHASE 5.1: Cleanup ciblé avec références aux handlers
  if (sharedState.socket === socket && floatingMessagingHandlers) {
    console.log('🔌 Socket déjà configuré, nettoyage ciblé des anciens listeners')

    // Nettoyer les CustomEvents (PHASE 5.1)
    if (floatingMessagingHandlers.handleNewMessageEvent) {
      window.removeEventListener(
        SOCKET_EVENTS.NEW_MESSAGE,
        floatingMessagingHandlers.handleNewMessageEvent,
      )
    }
    if (floatingMessagingHandlers.handleMessageDeletedEvent) {
      window.removeEventListener(
        SOCKET_EVENTS.MESSAGE_DELETED,
        floatingMessagingHandlers.handleMessageDeletedEvent,
      )
    }
    if (floatingMessagingHandlers.handleNewConversationEvent) {
      window.removeEventListener(
        SOCKET_EVENTS.NEW_CONVERSATION,
        floatingMessagingHandlers.handleNewConversationEvent,
      )
    }

    // Nettoyer les listeners socket restants
    socket.off('conversation_deleted', floatingMessagingHandlers.handleConversationDeleted)
    socket.off('message_read', floatingMessagingHandlers.handleMessageRead)
    socket.off('connect', floatingMessagingHandlers.handleConnect)
    socket.off('disconnect', floatingMessagingHandlers.handleDisconnect)
    socket.off('user_typing', floatingMessagingHandlers.handleUserTyping)
    floatingMessagingHandlers = null
  }

  console.log('🔌 Configuration des listeners Socket.IO pour la messagerie')
  sharedState.socket = socket
  sharedState.isSocketConnected = socket.connected

  // Nouveau message reçu avec vérification stricte de doublons
  const handleNewMessage = (message) => {
    // NOUVELLE VÉRIFICATION : L'utilisateur est-il toujours authentifié ?
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      console.log('🔓 Message reçu mais utilisateur non authentifié, ignoré')
      return
    }

    console.log('📨 Nouveau message reçu:', message)

    if (!message || !message.conversation_id || !message.id) return

    const conversationId = Number(message.conversation_id)
    const messageId = message.id

    if (messageExists(conversationId, messageId)) {
      console.log('⚠️ Message déjà traité, ignoré:', messageId)
      return
    }

    const currentUserId = getCurrentUserId()
    if (message.sender_id === currentUserId) {
      const alreadyInConversations = sharedState.openConversations.some(
        (conv) => conv.id === conversationId && conv.messages?.some((m) => m.id === messageId),
      )

      if (alreadyInConversations) {
        console.log('⚠️ Notre propre message déjà présent, ignoré:', messageId)
        sharedState.processedMessageIds.add(messageId)
        cleanupProcessedMessages()
        return
      }
    }

    let senderInfo = message.sender

    if (!senderInfo && message.sender_id) {
      // Chercher dans les conversations existantes
      const allConversations = [
        ...sharedState.recentConversations,
        ...sharedState.openConversations,
      ]

      for (const conv of allConversations) {
        if (conv.members) {
          const foundSender = conv.members.find((member) => member.id === message.sender_id)
          if (foundSender) {
            senderInfo = foundSender
            break
          }
        }
      }

      // Si toujours pas trouvé, créer un sender temporaire
      if (!senderInfo) {
        senderInfo = {
          id: message.sender_id,
          firstName: 'Chargement...',
          lastName: '',
          avatarUrl: null,
        }
      }
    }

    const normalizedMessage = {
      id: messageId,
      conversation_id: conversationId,
      sender_id: message.sender_id,
      content: message.content,
      created_at: message.created_at || message.createdAt || new Date().toISOString(),
      updated_at: message.updated_at || message.updatedAt || new Date().toISOString(),
      sender: senderInfo,
      is_system_message: message.is_system_message || false,
    }

    sharedState.processedMessageIds.add(messageId)

    // NOUVEAU: Vérifier si la conversation avait été supprimée localement
    const wasDeleted = sharedState.recentConversations.every((c) => c.id !== conversationId)
    if (wasDeleted) {
      console.log('🔄 Conversation supprimée qui reçoit un nouveau message, rechargement...')
      // Recharger les conversations pour récupérer la conversation "ressuscitée"
      loadRecentConversationsInternal()
      return
    }

    // Mettre à jour les conversations ouvertes
    sharedState.openConversations = sharedState.openConversations.map((conv) => {
      if (conv.id === conversationId) {
        const messageAlreadyExists = conv.messages?.some((m) => m.id === messageId)
        if (!messageAlreadyExists) {
          return {
            ...conv,
            messages: [...(conv.messages || []), normalizedMessage],
          }
        }
      }
      return conv
    })

    // Mettre à jour les conversations récentes
    const recentIndex = sharedState.recentConversations.findIndex((c) => c.id === conversationId)
    if (recentIndex !== -1) {
      const conversation = sharedState.recentConversations[recentIndex]

      const updatedConversation = {
        ...conversation,
        messages: [normalizedMessage],
        updatedAt: normalizedMessage.created_at,
      }

      if (normalizedMessage.sender_id !== currentUserId) {
        updatedConversation.unreadCount = (conversation.unreadCount || 0) + 1
        sharedState.totalUnread += 1
      }

      sharedState.recentConversations.splice(recentIndex, 1)
      sharedState.recentConversations.unshift(updatedConversation)
    }

    console.log('✅ Message ajouté via socket:', messageId)
    notifyListeners()
  }

  // NOUVEAU: Message supprimé reçu
  const handleMessageDeleted = (data) => {
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      return
    }

    console.log('🗑️ Message supprimé reçu:', data)

    const { messageId, conversationId, deletedBy, deleteForEveryone } = data
    const currentUserId = getCurrentUserId()

    // Si le message a été supprimé pour tout le monde, ou si c'est nous qui l'avons supprimé
    if (deleteForEveryone || String(deletedBy) === String(currentUserId)) {
      // Supprimer des conversations ouvertes
      sharedState.openConversations = sharedState.openConversations.map((conv) => {
        if (Number(conv.id) === Number(conversationId) && conv.messages) {
          return {
            ...conv,
            messages: conv.messages.filter((m) => Number(m.id) !== Number(messageId)),
          }
        }
        return conv
      })

      // Supprimer des conversations récentes (si c'était le dernier message)
      const recentIndex = sharedState.recentConversations.findIndex(
        (c) => Number(c.id) === Number(conversationId),
      )
      if (recentIndex !== -1) {
        const conversation = sharedState.recentConversations[recentIndex]
        if (Number(conversation.messages?.[0]?.id) === Number(messageId)) {
          // C'était le dernier message, il faut le remplacer ou vider
          // Ici on peut soit laisser vide soit recharger la conversation
          conversation.messages = []
        }
      }

      notifyListeners()
    }
  }

  // NOUVEAU: Conversation supprimée reçue
  const handleConversationDeleted = (data) => {
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      return
    }

    console.log('🗑️ Conversation supprimée reçue:', data)

    const { conversationId, deletedBy } = data
    const currentUserId = getCurrentUserId()

    // Si c'est nous qui avons supprimé, ou si c'était une suppression pour tous
    if (deletedBy === currentUserId) {
      // Supprimer des conversations ouvertes
      sharedState.openConversations = sharedState.openConversations.filter(
        (c) => c.id !== conversationId,
      )

      // Supprimer des conversations récentes
      sharedState.recentConversations = sharedState.recentConversations.filter(
        (c) => c.id !== conversationId,
      )

      // Recalculer les non lus
      sharedState.totalUnread = sharedState.recentConversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      )

      notifyListeners()
    }
  }

  // Nouvelle conversation
  const handleNewConversation = (conversationData) => {
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      return
    }

    console.log('💬 Nouvelle conversation:', conversationData)

    if (!conversationData || !conversationData.id) return

    const normalizedConv = {
      id: conversationData.id,
      type: conversationData.type || 'private',
      name: conversationData.name || null,
      // Normaliser participants vers members (le backend retourne participants)
      members: conversationData.members || conversationData.participants || [],
      messages: (conversationData.messages || []).map((m) => {
        const msg = {
          ...m,
          created_at: m.created_at || m.createdAt || new Date().toISOString(),
          updated_at: m.updated_at || m.updatedAt || new Date().toISOString(),
        }
        if (msg.id) {
          sharedState.processedMessageIds.add(msg.id)
        }
        return msg
      }),
      unreadCount: conversationData.unreadCount || 0,
      updatedAt:
        conversationData.updatedAt || conversationData.created_at || new Date().toISOString(),
      createdAt:
        conversationData.createdAt || conversationData.created_at || new Date().toISOString(),
    }

    const exists = sharedState.recentConversations.find((c) => c.id === normalizedConv.id)
    if (!exists) {
      sharedState.recentConversations.unshift(normalizedConv)
      if (sharedState.recentConversations.length > 10) {
        sharedState.recentConversations = sharedState.recentConversations.slice(0, 10)
      }
    }

    notifyListeners()
  }

  // États de connexion
  const handleConnect = () => {
    console.log('🔌 Socket connecté - messagerie')
    sharedState.isSocketConnected = true

    // NOUVELLE VÉRIFICATION : Ne rejoindre les conversations que si authentifié
    const authenticated = sharedState.isUserAuthenticated && checkAuthenticationStatus()
    if (authenticated) {
      sharedState.recentConversations.forEach((conv) => {
        socket.emit('join_conversation', conv.id)
      })
    }

    // Resync après une perte de connexion effective.
    //
    // Rejoindre les rooms ne suffit pas : rien ne rejoue ce qui a été manqué.
    // Vérifié en frappant le serveur — après une reconnexion il n'émet que
    // `user_status_change`. Le serveur n'a pas `connectionStateRecovery` et
    // `new_message` n'est diffusé qu'au moment de l'envoi. Un message reçu
    // pendant la coupure n'atteignait donc JAMAIS le client avant un
    // rechargement de page : compteur, ordre et aperçus restaient périmés.
    //
    // On repasse par le chemin canonique plutôt que de recalculer le seul
    // compteur : il remet d'aplomb les trois à la fois.
    //
    // Volontairement PAS au premier `connect` de la session : le chargement au
    // boot fait déjà le travail, déclencher ici créerait un double fetch.
    // `forceReloadConversations` réarme les garde-fous anti-rafale internes
    // (lastLoadTime, loading, initializationPromise) qui absorbent les
    // reconnexions en salve.
    if (sharedState.hasLostConnection && authenticated) {
      sharedState.hasLostConnection = false
      console.log('🔄 Reconnexion après coupure : resync des conversations')
      forceReloadConversations()
        .then(() =>
          // Les fenêtres restées ouvertes pendant la coupure ont manqué les
          // mêmes messages que la liste. Les laisser périmées donnerait une
          // liste juste et un contenu faux dans la même interface. On réutilise
          // le helper de récupération, dont le verrou empêche qu'un clic sur
          // notification et cette reconnexion rafraîchissent deux fois la même.
          Promise.all(
            sharedState.openConversations.map((conv) => refreshOpenConversation(conv.id)),
          ),
        )
        .catch((error) => {
          // Non bloquant : on retentera à la prochaine reconnexion.
          console.error('❌ Resync après reconnexion impossible:', error)
          sharedState.hasLostConnection = true
        })
    }

    notifyListeners()
  }

  const handleDisconnect = () => {
    console.log('🔌 Socket déconnecté - messagerie')
    sharedState.isSocketConnected = false
    // Marque la perte pour que le prochain `connect` sache qu'il s'agit d'une
    // reconnexion, et non de la connexion initiale.
    sharedState.hasLostConnection = true
    notifyListeners()
  }

  // Gérer les messages lus (read receipts)
  const handleMessageRead = ({ messageIds, conversationId, recipient }) => {
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      console.log('🔓 message_read reçu mais utilisateur non authentifié, ignoré')
      return
    }

    console.log('📖 Messages marqués comme lus:', {
      conversationId,
      messageIds,
      recipient,
    })

    if (!messageIds || messageIds.length === 0 || !recipient) {
      console.warn('⚠️ message_read incomplet:', { messageIds, recipient })
      return
    }

    // Mettre à jour les conversations ouvertes
    sharedState.openConversations = sharedState.openConversations.map((conv) => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: (conv.messages || []).map((msg) => {
            if (messageIds.includes(Number(msg.id))) {
              const notifications = msg.notifications || []

              // Vérifier si ce recipient n'est pas déjà dans les notifications
              const existingNotification = notifications.find(
                (n) => n.recipient && n.recipient.id === recipient.id,
              )

              if (!existingNotification) {
                notifications.push({ recipient })
              }

              return { ...msg, notifications }
            }
            return msg
          }),
        }
      }
      return conv
    })

    // Mettre à jour les conversations récentes aussi
    sharedState.recentConversations = sharedState.recentConversations.map((conv) => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: (conv.messages || []).map((msg) => {
            if (messageIds.includes(Number(msg.id))) {
              const notifications = msg.notifications || []

              const existingNotification = notifications.find(
                (n) => n.recipient && n.recipient.id === recipient.id,
              )

              if (!existingNotification) {
                notifications.push({ recipient })
              }

              return { ...msg, notifications }
            }
            return msg
          }),
        }
      }
      return conv
    })

    notifyListeners()
  }

  // Gérer l'indicateur "en train d'écrire"
  const handleUserTyping = ({ userId, isTyping, conversationId }) => {
    if (!sharedState.isUserAuthenticated || !checkAuthenticationStatus()) {
      return
    }

    if (isTyping) {
      // Ajouter l'utilisateur aux typingUsers s'il n'y est pas déjà
      const existingIndex = sharedState.typingUsers.findIndex(
        (t) => t.conversationId === conversationId && t.userId === userId,
      )

      if (existingIndex === -1) {
        // IMPORTANT: Créer un NOUVEAU tableau pour que React détecte le changement
        sharedState.typingUsers = [
          ...sharedState.typingUsers,
          { conversationId, userId, isTyping: true },
        ]
      } else {
        // IMPORTANT: Créer un NOUVEAU tableau avec l'élément mis à jour
        sharedState.typingUsers = sharedState.typingUsers.map((t, idx) =>
          idx === existingIndex ? { ...t, isTyping: true } : t,
        )
      }
    } else {
      // Retirer l'utilisateur des typingUsers
      sharedState.typingUsers = sharedState.typingUsers.filter(
        (t) => !(t.conversationId === conversationId && t.userId === userId),
      )
    }

    notifyListeners()
  }

  // ✅ PHASE 5.1: Wrappers pour écouter les CustomEvents au lieu de socket.on direct
  const handleNewMessageEvent = (event) => {
    const { message } = event.detail
    if (message) handleNewMessage(message)
  }

  const handleMessageDeletedEvent = (event) => {
    const { messageId, conversationId, deletedBy, deleteForEveryone } = event.detail
    if (messageId && conversationId)
      handleMessageDeleted({ messageId, conversationId, deletedBy, deleteForEveryone })
  }

  const handleNewConversationEvent = (event) => {
    const { conversation } = event.detail
    if (conversation) handleNewConversation(conversation)
  }

  // ✅ PHASE 5.1: Stocker les références aux handlers pour cleanup ciblé
  floatingMessagingHandlers = {
    handleNewMessage,
    handleMessageDeleted,
    handleConversationDeleted,
    handleNewConversation,
    handleMessageRead,
    handleConnect,
    handleUserTyping,
    handleDisconnect,
    // Nouveaux handlers CustomEvent
    handleNewMessageEvent,
    handleMessageDeletedEvent,
    handleNewConversationEvent,
  }

  // ✅ PHASE 5.1: Écouter les CustomEvents centralisés (au lieu de socket.on direct)
  // new_message, message_deleted, new_conversation sont maintenant gérés par SocketContext
  window.addEventListener(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessageEvent)
  window.addEventListener(SOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeletedEvent)
  window.addEventListener(SOCKET_EVENTS.NEW_CONVERSATION, handleNewConversationEvent)

  // Ces événements restent sur le socket car ils ne sont pas centralisés
  socket.on('conversation_deleted', handleConversationDeleted)
  socket.on('message_read', handleMessageRead)
  socket.on('connect', handleConnect)
  socket.on('user_typing', handleUserTyping)
  socket.on('disconnect', handleDisconnect)

  if (socket.connected) {
    handleConnect()
  }

  // Fonction de nettoyage
  const cleanup = () => {
    if (sharedState.socket === socket) {
      console.log('🧹 Nettoyage des listeners Socket.IO pour la messagerie')

      // ✅ PHASE 5.1: Nettoyer les CustomEvents
      window.removeEventListener(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessageEvent)
      window.removeEventListener(SOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeletedEvent)
      window.removeEventListener(SOCKET_EVENTS.NEW_CONVERSATION, handleNewConversationEvent)

      // Nettoyer les listeners socket restants
      socket.off('conversation_deleted', handleConversationDeleted)
      socket.off('message_read', handleMessageRead)
      socket.off('connect', handleConnect)
      socket.off('user_typing', handleUserTyping)
      socket.off('disconnect', handleDisconnect)

      // ✅ PHASE 5 FIX: Nettoyer aussi la référence globale
      floatingMessagingHandlers = null
      sharedState.socket = null
      sharedState.isSocketConnected = false
    }
  }

  return cleanup
}

// Fonction utilitaire pour obtenir l'ID utilisateur actuel
const getCurrentUserId = () => {
  const state = window.store?.getState()
  return state?.user?.user?.id || localStorage.getItem('userId')
}

// NOUVELLE FONCTION: Charger conversations avec protection StrictMode
const loadRecentConversationsInternal = async () => {
  if (!checkAuthenticationStatus()) {
    console.log('🔓 Tentative de chargement sans authentification, arrêt')
    resetMessagingState()
    return
  }

  if (sharedState.isInitializing && sharedState.initializationPromise) {
    return sharedState.initializationPromise
  }

  if (sharedState.loading) {
    return sharedState.loadPromise
  }

  const now = Date.now()
  if (now - sharedState.lastLoadTime < 1000) {
    return
  }

  try {
    sharedState.loading = true
    sharedState.isInitializing = true
    sharedState.lastLoadTime = now

    sharedState.loadPromise = (async () => {
      // ✅ SECURITY: Vérifier userId (le cookie httpOnly gère l'auth)
      const userId = localStorage.getItem('userId')
      if (!userId) throw new Error('Non authentifié')

      console.log('🌐 API Call: récupération de toutes les conversations')

      try {
        const conversations = await get('/messaging/conversations/recent')

        const normalizedConversations = conversations.map((conv) => ({
          ...conv,
          members: conv.members || conv.participants || [],
        }))

        sharedState.recentConversations = normalizedConversations

        sharedState.totalUnread = normalizedConversations.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0,
        )

        normalizedConversations.forEach((conv) => {
          conv.messages?.forEach((msg) => {
            if (msg.id) sharedState.processedMessageIds.add(msg.id)
          })
        })
        cleanupProcessedMessages()

        if (sharedState.socket && sharedState.isSocketConnected) {
          normalizedConversations.forEach((conv) => {
            sharedState.socket.emit('join_conversation', conv.id)
          })
        }

        return normalizedConversations
      } catch (error) {
        if (error.message?.includes('401') || error.message?.includes('403')) {
          resetMessagingState()
          return
        }
        throw error
      }
    })()

    sharedState.initializationPromise = sharedState.loadPromise

    await sharedState.loadPromise
  } catch (error) {
    console.error('❌ SINGLETON: Erreur rechargement conversations:', error)
    if (error.message.includes('401') || error.message.includes('403')) {
      resetMessagingState()
    }
    throw error
  } finally {
    sharedState.loading = false
    sharedState.isInitializing = false
    sharedState.loadPromise = null
    sharedState.initializationPromise = null
    notifyListeners()
  }
}

// FONCTION POUR FORCER LE RECHARGEMENT
const forceReloadConversations = async () => {
  // NOUVELLE VÉRIFICATION
  if (!checkAuthenticationStatus()) {
    console.log('🔓 Force reload sans authentification, reset')
    resetMessagingState()
    return
  }

  console.log('🔄 FORCE RELOAD: Rechargement forcé des conversations')

  sharedState.hasInitialized = false
  sharedState.loading = false
  sharedState.isInitializing = false
  sharedState.lastLoadTime = 0
  sharedState.loadPromise = null
  sharedState.initializationPromise = null
  sharedState.processedMessageIds.clear()

  await loadRecentConversationsInternal()

  sharedState.hasInitialized = true
}

// Chargement des détails d'une conversation. Extrait au niveau module pour être
// PARTAGÉ par l'ouverture depuis une notification et le resync de reconnexion,
// plutôt que dupliqué dans les deux chemins.
const loadConversationDetailsInternal = async (conversationId) => {
  if (!checkAuthenticationStatus()) {
    resetMessagingState()
    return null
  }

  try {
    let baseConv = sharedState.recentConversations.find((c) => c.id === conversationId)

    if (!baseConv) {
      try {
        const convDetails = await get(`/messaging/conversations/${conversationId}/details`)
        if (convDetails?.conversation) baseConv = convDetails.conversation
      } catch (apiError) {
        console.error('⚠️ SINGLETON: Détails de conversation indisponibles:', apiError)
      }
    }

    const messagesResponse = await get(
      `/messaging/conversations/${conversationId}/messages?limit=50`,
    )
    const messages = messagesResponse.rows || messagesResponse.messages || []
    const totalCount = messagesResponse.count || messages.length

    messages.forEach((msg) => {
      if (msg.id) sharedState.processedMessageIds.add(msg.id)
    })

    const conversation = {
      id: conversationId,
      type: baseConv?.type || 'private',
      name: baseConv?.name || null,
      // Normaliser participants vers members (le backend retourne participants)
      members: baseConv?.members || baseConv?.participants || [],
      messages,
      unreadCount: baseConv?.unreadCount || 0,
      totalMessagesCount: totalCount,
      currentPage: 1,
      hasMoreMessages: messages.length >= 50 && totalCount > messages.length,
    }

    if (sharedState.socket && sharedState.isSocketConnected) {
      sharedState.socket.emit('join_conversation', conversationId)
    }

    return conversation
  } catch (error) {
    console.error('❌ SINGLETON: Erreur chargement détails:', error)
    return null
  }
}

// Rafraîchit UNE conversation déjà ouverte depuis le serveur.
//
// L'état purement UI de la fenêtre est conservé (`isMinimized`) : seules les
// données serveur sont remplacées, et l'ordre du tableau est préservé par le
// `map`. Le verrou est celui utilisé par l'ouverture depuis une notification :
// un clic et une reconnexion concurrents ne déclenchent donc qu'un seul fetch
// pour la même conversation.
const refreshOpenConversation = async (conversationId) => {
  const refreshKey = `refresh_${conversationId}`
  if (window.__CONVERSATION_LOCKS__.has(refreshKey)) {
    return window.__CONVERSATION_LOCKS__.get(refreshKey)
  }

  const refreshPromise = (async () => {
    try {
      const fresh = await loadConversationDetailsInternal(conversationId)
      if (!fresh) return

      sharedState.openConversations = sharedState.openConversations.map((c) =>
        c.id === conversationId ? { ...fresh, isMinimized: c.isMinimized } : c,
      )
      notifyListeners()
    } catch (error) {
      // Non bloquant : la fenêtre reste sur son état précédent.
      console.error('❌ SINGLETON: Erreur resync conversation ouverte:', error)
    } finally {
      window.__CONVERSATION_LOCKS__.delete(refreshKey)
    }
  })()

  window.__CONVERSATION_LOCKS__.set(refreshKey, refreshPromise)
  return refreshPromise
}

// ===== LECTURE SEULE DU COMPTEUR DE NON-LUS =====
//
// `subscribe` ajoute/retire le callback dans `sharedState.listeners`,
// `getSnapshot` renvoie `sharedState.totalUnread`. Rien d'autre : aucune
// minuterie, aucun listener socket, aucun chargement de conversations, aucune
// mutation. C'est tout le contrat.
//
// Pourquoi ne PAS appeler useFloatingMessaging pour lire ce nombre : chacun de
// ses appels déclenche cinq effets, dont deux ne sont gardés que par instance
// (`setupAuthenticationWatcher` et `setupSocketListeners`). Un second appelant
// ajouterait donc une minuterie d'authentification de 30 s et provoquerait un
// cycle détache/rattache des listeners socket à chaque montage. Ce hook-là a
// été écrit pour un appelant unique, FloatingMessagingManager.
//
// useSyncExternalStore convient exactement : `notifyListeners()` est déjà
// l'unique mécanisme de signalement du singleton, et le snapshot est un nombre
// — donc pas de re-rendu en boucle par identité d'objet. Il relit aussi le
// snapshot à chaque rendu, ce qui rattrape le throttle de 50 ms de
// `notifyListeners` : une notification avalée ne fige pas la valeur.
//
// Les deux fonctions sont définies au niveau module, donc stables : React ne
// se réabonne pas à chaque rendu.
const subscribeToUnreadCount = (onStoreChange) => {
  sharedState.listeners.add(onStoreChange)
  return () => {
    sharedState.listeners.delete(onStoreChange)
  }
}

const getUnreadCountSnapshot = () => sharedState.totalUnread

/**
 * Nombre de messages non lus, depuis la source canonique de la messagerie.
 *
 * Destiné aux surfaces qui doivent afficher ce compteur sans piloter la
 * messagerie : titre d'onglet, badge de navigation, `navigator.setAppBadge()`.
 *
 * @returns {number}
 */
export const useMessagingUnreadCount = () =>
  useSyncExternalStore(subscribeToUnreadCount, getUnreadCountSnapshot, getUnreadCountSnapshot)

export const useFloatingMessaging = () => {
  const currentUser = useSelector((state) => state.user?.user)
  const { socket } = useSocketContext()
  const [, forceUpdate] = useState({})

  const instanceIdRef = useRef(++sharedState.instanceCount)
  const instanceId = instanceIdRef.current

  // PROTECTION StrictMode avec useRef
  const hasInitializedRef = useRef(false)
  const cleanupFunctionRef = useRef(null)
  const authWatcherCleanupRef = useRef(null)

  if (sharedState.instanceCount % 10 === 0) {
    console.log(`🔄 Instance ${instanceId} rendue - Total: ${sharedState.instanceCount}`)
  }

  const forceRerender = useCallback(() => {
    if (sharedState.instanceCount % 10 === 0) {
      console.log(`🔄 Instance ${instanceId} - Force rerender`)
    }
    forceUpdate({})
  }, [instanceId])

  // S'abonner aux changements de l'état partagé
  useEffect(() => {
    sharedState.listeners.add(forceRerender)

    return () => {
      sharedState.listeners.delete(forceRerender)
      sharedState.instanceCount--
    }
  }, [forceRerender, instanceId])

  // NOUVEAU : Configuration du watcher d'authentification
  useEffect(() => {
    if (!authWatcherCleanupRef.current) {
      authWatcherCleanupRef.current = setupAuthenticationWatcher()
    }

    return () => {
      if (authWatcherCleanupRef.current) {
        authWatcherCleanupRef.current()
        authWatcherCleanupRef.current = null
      }
    }
  }, [])

  // Configuration du socket avec protection StrictMode
  useEffect(() => {
    console.log(`🔌 Instance ${instanceId} - Vérification socket:`, {
      socketExists: !!socket,
      userId: currentUser?.id,
      sharedStateSocket: !!sharedState.socket,
    })

    if (socket && currentUser?.id) {
      console.log(`🔌 Instance ${instanceId} - Configuration Socket.IO`)
      const cleanup = setupSocketListeners(socket)
      cleanupFunctionRef.current = cleanup

      return cleanup
    }
  }, [socket, currentUser?.id, instanceId])

  // ✅ SECURITY: API calls avec vérification d'authentification via userId
  const apiCall = async (endpoint, options = {}) => {
    if (!checkAuthenticationStatus()) {
      console.log('🔓 API Call sans authentification, reset')
      resetMessagingState()
      throw new Error('Non authentifié')
    }

    // Note: Le cookie httpOnly gère l'auth - pas besoin de token ici
    try {
      const method = options.method || 'GET'
      const body = options.body ? JSON.parse(options.body) : undefined

      let response
      switch (method.toUpperCase()) {
        case 'GET':
          response = await get(endpoint)
          break
        case 'POST':
          response = await post(endpoint, body)
          break
        case 'PUT':
          response = await put(endpoint, body)
          break
        case 'DELETE':
          response = await del(endpoint, body)
          break
        default:
          throw new Error(`Méthode HTTP non supportée: ${method}`)
      }

      return response
    } catch (error) {
      // Gestion des erreurs d'authentification
      if (error.message?.includes('401') || error.message?.includes('403')) {
        console.log('🔓 API Call 401/403, reset messagerie')
        resetMessagingState()
        throw new Error('Non authentifié')
      }

      console.error(`❌ API Error:`, error)
      throw error
    }
  }

  // Charger conversations avec protection contre les boucles et StrictMode
  const loadRecentConversations = useCallback(async () => {
    if (!currentUser?.id) {
      console.log("❌ Pas d'utilisateur connecté, arrêt du chargement")
      return
    }

    // NOUVELLE VÉRIFICATION
    if (!checkAuthenticationStatus()) {
      console.log('🔓 Load conversations sans authentification')
      resetMessagingState()
      return
    }

    // Protection StrictMode : vérifier si cette instance a déjà initialisé
    if (hasInitializedRef.current) {
      console.log(`⏸️ Instance ${instanceId} - Déjà initialisée, arrêt`)
      return
    }

    const shouldLoad =
      !sharedState.hasInitialized &&
      !sharedState.loading &&
      !sharedState.isInitializing &&
      sharedState.recentConversations.length === 0

    if (!shouldLoad) {
      console.log(`⏸️ Instance ${instanceId} - Conditions non remplies pour le chargement:`, {
        hasInitialized: sharedState.hasInitialized,
        loading: sharedState.loading,
        isInitializing: sharedState.isInitializing,
        conversationsCount: sharedState.recentConversations.length,
      })
      return
    }

    try {
      console.log(`🏁 Instance ${instanceId} - Début initialisation pour userId:`, currentUser.id)
      hasInitializedRef.current = true // Marquer cette instance comme initialisée
      sharedState.hasInitialized = true
      // NOUVEAU : Marquer l'utilisateur comme authentifié
      sharedState.currentUserId = currentUser.id
      sharedState.isUserAuthenticated = true

      await loadRecentConversationsInternal()
    } catch (error) {
      console.error('❌ SINGLETON: Erreur chargement conversations:', error)
      sharedState.hasInitialized = false
      hasInitializedRef.current = false // Reset en cas d'erreur
    }
  }, [currentUser?.id, instanceId])

  // Charger détails d'une conversation
  // Délègue au helper module, partagé avec le resync de reconnexion.
  const loadConversationDetails = useCallback(
    (conversationId) => loadConversationDetailsInternal(conversationId),
    [],
  )

  // NOUVEAU: Charger plus de messages pour une conversation ouverte (pagination)
  const loadMoreMessages = useCallback(async (conversationId, currentPage) => {
    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      return null
    }

    try {
      const nextPage = currentPage + 1
      console.log('📄 SINGLETON: Chargement page', nextPage, 'pour conversation', conversationId)

      const messagesResponse = await apiCall(
        `/messaging/conversations/${conversationId}/messages?limit=50&page=${nextPage}`,
      )

      const messages = messagesResponse.rows || messagesResponse.messages || []
      const totalCount = messagesResponse.count || 0

      // Marquer tous les messages comme traités
      messages.forEach((msg) => {
        if (msg.id) {
          sharedState.processedMessageIds.add(msg.id)
        }
      })

      console.log('✅ SINGLETON: Page', nextPage, 'chargée:', messages.length, 'messages')

      return {
        messages,
        totalCount,
        currentPage: nextPage,
        hasMoreMessages: messages.length >= 50 && totalCount > nextPage * 50,
      }
    } catch (error) {
      console.error('❌ SINGLETON: Erreur chargement messages supplémentaires:', error)
      return null
    }
  }, [])

  // Autres fonctions (avec vérifications d'authentification ajoutées)
  const openConversation = useCallback(
    async (conversationId) => {
      if (!checkAuthenticationStatus()) {
        resetMessagingState()
        return
      }

      console.log(`🚀 Instance ${instanceId} - Ouverture conversation:`, conversationId)

      const existing = sharedState.openConversations.find((c) => c.id === conversationId)
      if (existing) {
        console.log('♻️ SINGLETON: Conversation déjà ouverte, dé-minimisation + resync')

        // 1. Dé-minimisation immédiate : retour visuel sans attendre le réseau.
        sharedState.openConversations = sharedState.openConversations.map((c) =>
          c.id === conversationId ? { ...c, isMinimized: false } : c,
        )
        notifyListeners()

        // 2. Resynchronisation. La fenêtre a pu rester ouverte ou minimisée
        // pendant que des messages arrivaient sans être reçus par le socket
        // (déconnexion, onglet en veille, room non rejointe). Sans ce refetch,
        // cliquer sur la notification rouvrait un état périmé. loadConversationDetails
        // ré-émet aussi join_conversation, ce qui répare la room au passage.
        // Le helper conserve `isMinimized`, qu'on vient de remettre à false.
        return refreshOpenConversation(conversationId)
      }

      const conversation = await loadConversationDetails(conversationId)
      if (!conversation) {
        console.error('❌ SINGLETON: Impossible de charger la conversation')
        return
      }

      const newConv = { ...conversation, isMinimized: false }
      const filtered = sharedState.openConversations.filter((c) => c.id !== conversationId)
      sharedState.openConversations = [newConv, ...filtered].slice(0, 3)

      console.log(
        '✅ SINGLETON: Conversations ouvertes:',
        sharedState.openConversations.map((c) => c.id),
      )
      notifyListeners()
    },
    [loadConversationDetails, instanceId],
  )

  const closeConversation = useCallback(
    (conversationId) => {
      console.log(`❌ Instance ${instanceId} - Fermeture conversation:`, conversationId)
      sharedState.openConversations = sharedState.openConversations.filter(
        (c) => c.id !== conversationId,
      )

      if (sharedState.socket && sharedState.isSocketConnected) {
        sharedState.socket.emit('leave_conversation', conversationId)
      }

      notifyListeners()
    },
    [instanceId],
  )

  const toggleMinimizeConversation = useCallback(
    (conversationId) => {
      console.log(`📦 Instance ${instanceId} - Toggle minimize:`, conversationId)
      sharedState.openConversations = sharedState.openConversations.map((c) =>
        c.id === conversationId ? { ...c, isMinimized: !c.isMinimized } : c,
      )
      notifyListeners()
    },
    [instanceId],
  )

  // Envoi de message SIMPLIFIÉ sans messages temporaires
  const sendMessage = useCallback(async (conversationId, content, meta) => {
    if (!content.trim()) return

    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      throw new Error('Non authentifié')
    }

    try {
      console.log('📤 SINGLETON: Envoi message vers:', conversationId)

      const newMessage = await apiCall(`/messaging/conversations/${conversationId}/messages`, {
        method: 'POST',
        // meta.radarMeta (A′.1) : attribution funnel radar → message (optionnel).
        body: JSON.stringify({ content: content.trim(), ...(meta ? { radarMeta: meta } : {}) }),
      })

      const normalizedMessage = {
        ...newMessage,
        created_at: newMessage.created_at || newMessage.createdAt || new Date().toISOString(),
        updated_at: newMessage.updated_at || newMessage.updatedAt || new Date().toISOString(),
      }

      sharedState.processedMessageIds.add(normalizedMessage.id)

      // Mettre à jour les conversations ouvertes
      sharedState.openConversations = sharedState.openConversations.map((conv) => {
        if (conv.id === conversationId) {
          const messageExists = conv.messages?.some((m) => m.id === normalizedMessage.id)
          if (!messageExists) {
            return {
              ...conv,
              messages: [...(conv.messages || []), normalizedMessage],
              unreadCount: 0,
            }
          }
        }
        return conv
      })

      // Mettre à jour la liste des conversations récentes
      const recentConvIndex = sharedState.recentConversations.findIndex(
        (c) => c.id === conversationId,
      )
      if (recentConvIndex !== -1) {
        const updatedConv = {
          ...sharedState.recentConversations[recentConvIndex],
          messages: [normalizedMessage],
          updatedAt: normalizedMessage.created_at,
          unreadCount: 0,
        }

        sharedState.recentConversations.splice(recentConvIndex, 1)
        sharedState.recentConversations.unshift(updatedConv)
      }

      sharedState.totalUnread = sharedState.recentConversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      )

      console.log('✅ SINGLETON: Message envoyé, ID:', normalizedMessage.id)
      notifyListeners()

      return normalizedMessage
    } catch (error) {
      console.error('❌ SINGLETON: Erreur envoi message:', error)
      throw error
    }
  }, [])

  // NOUVEAU: Fonction de suppression de conversation
  const deleteConversation = useCallback(async (conversationId) => {
    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      throw new Error('Non authentifié')
    }

    try {
      console.log('🗑️ SINGLETON: Suppression conversation:', conversationId)

      await apiCall(`/messaging/conversations/${conversationId}`, {
        method: 'DELETE',
      })

      // Supprimer localement des conversations ouvertes
      sharedState.openConversations = sharedState.openConversations.filter(
        (c) => c.id !== conversationId,
      )

      // Supprimer localement des conversations récentes
      sharedState.recentConversations = sharedState.recentConversations.filter(
        (c) => c.id !== conversationId,
      )

      // Recalculer les non lus
      sharedState.totalUnread = sharedState.recentConversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      )

      // Quitter la room socket
      if (sharedState.socket && sharedState.isSocketConnected) {
        sharedState.socket.emit('leave_conversation', conversationId)
      }

      console.log('✅ SINGLETON: Conversation supprimée localement')
      notifyListeners()
    } catch (error) {
      console.error('❌ SINGLETON: Erreur suppression conversation:', error)
      throw error
    }
  }, [])

  // NOUVEAU: Fonction de suppression de message
  const deleteMessage = useCallback(async (messageId, deleteForEveryone = false) => {
    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      throw new Error('Non authentifié')
    }

    try {
      console.log('🗑️ SINGLETON: Suppression message:', messageId, 'pour tous:', deleteForEveryone)

      await apiCall(`/messaging/messages/${messageId}`, {
        method: 'DELETE',
        body: JSON.stringify({ deleteForEveryone }),
      })

      // Supprimer localement immédiatement pour une UX fluide
      // (le socket se chargera de la synchronisation)
      sharedState.openConversations = sharedState.openConversations.map((conv) => {
        if (conv.messages) {
          return {
            ...conv,
            messages: conv.messages.filter((m) => m.id !== messageId),
          }
        }
        return conv
      })

      // Mettre à jour les conversations récentes si c'était le dernier message
      sharedState.recentConversations = sharedState.recentConversations.map((conv) => {
        if (conv.messages?.[0]?.id === messageId) {
          // C'était le dernier message visible, le retirer
          return {
            ...conv,
            messages: [],
          }
        }
        return conv
      })

      console.log('✅ SINGLETON: Message supprimé localement')
      notifyListeners()
    } catch (error) {
      console.error('❌ SINGLETON: Erreur suppression message:', error)
      throw error
    }
  }, [])

  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      return
    }

    try {
      await apiCall(`/messaging/conversations/${conversationId}/markAsRead`, {
        method: 'PUT',
      })

      sharedState.recentConversations = sharedState.recentConversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv,
      )

      sharedState.totalUnread = sharedState.recentConversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      )

      console.log('✅ SINGLETON: Conversation marquée comme lue')
      notifyListeners()
    } catch (error) {
      console.error('❌ SINGLETON: Erreur marquage lu:', error)
    }
  }, [])

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) return []

    if (!checkAuthenticationStatus()) {
      resetMessagingState()
      return []
    }

    try {
      const users = await apiCall(`/messaging/users/search?query=${encodeURIComponent(query)}`)
      return users
    } catch (error) {
      console.error('❌ SINGLETON: Erreur recherche utilisateurs:', error)
      return []
    }
  }, [])

  const createConversation = useCallback(
    async (memberIds, type = 'private', name = null) => {
      if (!checkAuthenticationStatus()) {
        resetMessagingState()
        throw new Error('Non authentifié')
      }

      try {
        const newConversation = await apiCall('/messaging/conversations', {
          method: 'POST',
          body: JSON.stringify({ memberIds, type, name }),
        })

        // Normaliser participants vers members (le backend retourne participants)
        const normalizedConversation = {
          ...newConversation,
          members: newConversation.members || newConversation.participants || [],
        }

        sharedState.recentConversations = [
          normalizedConversation,
          ...sharedState.recentConversations.slice(0, 9),
        ]
        notifyListeners()

        setTimeout(() => openConversation(normalizedConversation.id), 100)

        return normalizedConversation
      } catch (error) {
        console.error('❌ SINGLETON: Erreur création conversation:', error)
        throw error
      }
    },
    [openConversation],
  )

  const setIsDockOpen = useCallback((value) => {
    // NOUVELLE VÉRIFICATION : Ne pas permettre d'ouvrir le dock si non authentifié
    if (value && !checkAuthenticationStatus()) {
      console.log("🔓 Tentative d'ouverture du dock sans authentification")
      resetMessagingState()
      return
    }

    sharedState.isDockOpen = value
    notifyListeners()
  }, [])

  // Initialisation avec protection StrictMode améliorée
  useEffect(() => {
    if (currentUser?.id) {
      // NOUVEAU : Vérifier l'authentification avant l'initialisation
      if (!checkAuthenticationStatus()) {
        console.log('🔓 Utilisateur présent mais non authentifié, reset')
        resetMessagingState()
        return
      }

      // Délai plus court mais avec protection par référence
      const timer = setTimeout(() => {
        if (!hasInitializedRef.current && checkAuthenticationStatus()) {
          loadRecentConversations()
        }
      }, 100)

      return () => clearTimeout(timer)
    } else {
      // Reset complet si l'utilisateur se déconnecte
      console.log("🔓 Pas d'utilisateur, reset complet de la messagerie")
      resetMessagingState()
      hasInitializedRef.current = false

      if (cleanupFunctionRef.current) {
        cleanupFunctionRef.current()
        cleanupFunctionRef.current = null
      }

      notifyListeners()
    }
  }, [currentUser?.id, loadRecentConversations])

  // Debug réduit
  if (sharedState.instanceCount % 20 === 0) {
    console.log(`📊 Instance ${instanceId} - État:`, {
      userId: currentUser?.id,
      isUserAuthenticated: sharedState.isUserAuthenticated,
      recentConversations: sharedState.recentConversations.length,
      hasInitialized: sharedState.hasInitialized,
      hasInitializedRef: hasInitializedRef.current,
      loading: sharedState.loading,
      processedMessages: sharedState.processedMessageIds.size,
    })
  }

  // NOUVEAU : Écouter TOUS les événements d'ouverture/création de conversation
  useEffect(() => {
    if (
      sharedState.globalEventHandlerInstance &&
      sharedState.globalEventHandlerInstance !== instanceId
    ) {
      console.log(
        `⏸️ Instance ${instanceId} - Événements globaux déjà gérés par l'instance ${sharedState.globalEventHandlerInstance}`,
      )
      return
    }

    if (!sharedState.globalEventHandlerInstance) {
      sharedState.globalEventHandlerInstance = instanceId
      console.log(`🎯 Instance ${instanceId} - Devient le gestionnaire d'événements globaux`)
    }
    // Protection globale contre les événements multiples
    const eventProcessingMap = new Map() // memberId -> timestamp

    // 1. Ouvrir une conversation existante (depuis notification) - INCHANGÉ
    const handleOpenConversationEvent = (event) => {
      if (event.detail && event.detail.conversationId) {
        console.log('Ouverture conversation existante:', event.detail.conversationId)
        openConversation(event.detail.conversationId)
      }
    }

    // 2. CORRECTION MAJEURE : Créer/ouvrir conversation avec un membre
    const handleCreateConversationEvent = async (event) => {
      if (!event.detail || !event.detail.memberId) return

      const memberId = Number(event.detail.memberId)
      const currentUserId = Number(getCurrentUserId())
      const now = Date.now()

      if (memberId === currentUserId) {
        console.warn('Tentative de conversation avec soi-même, ignorée')
        return
      }

      // Verrou pour éviter les doublons
      const lockKey = `create_${memberId}`

      if (window.__CONVERSATION_LOCKS__.has(lockKey)) {
        console.log('⏳ Opération déjà en cours pour membre:', memberId)
        return window.__CONVERSATION_LOCKS__.get(lockKey)
      }

      const lastProcessed = eventProcessingMap.get(memberId) || 0
      if (now - lastProcessed < 1000) {
        console.log('⏸️ Événement ignoré - traitement récent pour membre:', memberId)
        return
      }
      eventProcessingMap.set(memberId, now)

      const promise = (async () => {
        try {
          console.log('🔄 Traitement création/ouverture conversation pour membre:', memberId)

          const openConv = findOpenConversationWithMember(memberId)
          if (openConv) {
            console.log('✓ Conversation déjà ouverte trouvée:', openConv.id)
            if (openConv.isMinimized) {
              await openConversation(openConv.id)
            }
            return
          }

          await ensureRecentConversationsLoaded()
          const existingConv = findExistingConversationWithMember(memberId)

          if (existingConv) {
            console.log('✓ Conversation existante trouvée:', existingConv.id)
            await openConversation(existingConv.id)
          } else {
            console.log('➕ Création nouvelle conversation avec membre:', memberId)
            await createConversation([memberId], 'private')
          }
        } catch (error) {
          console.error('❌ Erreur gestion conversation:', error)
        } finally {
          window.__CONVERSATION_LOCKS__.delete(lockKey)
          eventProcessingMap.delete(memberId)
        }
      })()

      window.__CONVERSATION_LOCKS__.set(lockKey, promise)
      return promise
    }

    // 3. CORRECTION : Conversation avec message prérempli
    const handleCreateConversationWithMessageEvent = async (event) => {
      if (!event.detail || !event.detail.memberId) return

      const memberId = Number(event.detail.memberId)
      const currentUserId = Number(getCurrentUserId())
      const now = Date.now()

      // Protection 1 : Pas de conversation avec soi-même
      if (memberId === currentUserId) {
        console.warn('Tentative de conversation avec message avec soi-même, ignorée')
        return
      }

      // Protection 2 : Éviter les événements multiples
      const lastProcessed = eventProcessingMap.get(`${memberId}_msg`) || 0
      if (now - lastProcessed < 1000) {
        console.log('Événement message ignoré - traitement récent pour membre:', memberId)
        return
      }
      eventProcessingMap.set(`${memberId}_msg`, now)

      console.log('Traitement conversation avec message pour membre:', memberId)

      try {
        // Protection 3 : Vérifier conversations ouvertes
        const openConv = findOpenConversationWithMember(memberId)
        if (openConv) {
          console.log('Conversation ouverte trouvée pour message:', openConv.id)
          if (openConv.isMinimized) {
            await openConversation(openConv.id)
          }

          if (event.detail.message) {
            setTimeout(() => {
              sendMessage(openConv.id, event.detail.message)
            }, 300)
          }
          return
        }

        // Protection 4 : S'assurer d'avoir les données récentes
        await ensureRecentConversationsLoaded()

        // Protection 5 : Rechercher dans les récentes
        const existingConv = findExistingConversationWithMember(memberId)

        if (existingConv) {
          console.log('Conversation existante trouvée pour message:', existingConv.id)
          await openConversation(existingConv.id)

          if (event.detail.message) {
            setTimeout(() => {
              sendMessage(existingConv.id, event.detail.message)
            }, 500)
          }
        } else {
          console.log('Création nouvelle conversation avec message...')
          const conversation = await createConversation([memberId], 'private')

          if (event.detail.message && conversation?.id) {
            setTimeout(() => {
              sendMessage(conversation.id, event.detail.message)
            }, 500)
          }
        }
      } catch (error) {
        console.error('Erreur conversation avec message:', error)
        eventProcessingMap.delete(`${memberId}_msg`)
      }
    }

    // Nettoyage périodique de la map des événements traités
    const cleanupInterval = setInterval(() => {
      const now = Date.now()
      for (const [key, timestamp] of eventProcessingMap.entries()) {
        if (now - timestamp > 5000) {
          // Nettoyer après 5 secondes
          eventProcessingMap.delete(key)
        }
      }
    }, 10000) // Toutes les 10 secondes

    // Ajouter les listeners avec options optimisées
    const eventOptions = { passive: true, capture: false }

    window.addEventListener('openFloatingConversation', handleOpenConversationEvent, eventOptions)
    window.addEventListener(
      'createFloatingConversation',
      handleCreateConversationEvent,
      eventOptions,
    )
    window.addEventListener(
      'createFloatingConversationWithMessage',
      handleCreateConversationWithMessageEvent,
      eventOptions,
    )

    return () => {
      if (sharedState.globalEventHandlerInstance === instanceId) {
        sharedState.globalEventHandlerInstance = null
        console.log(`🧹 Instance ${instanceId} - Libère le gestionnaire d'événements globaux`)
      }
      // Nettoyage complet
      clearInterval(cleanupInterval)
      eventProcessingMap.clear()

      window.removeEventListener('openFloatingConversation', handleOpenConversationEvent)
      window.removeEventListener('createFloatingConversation', handleCreateConversationEvent)
      window.removeEventListener(
        'createFloatingConversationWithMessage',
        handleCreateConversationWithMessageEvent,
      )
    }
  }, [openConversation, createConversation, sendMessage])

  // NOUVEAU : Retourner des valeurs vides si non authentifié
  if (!sharedState.isUserAuthenticated || !currentUser?.id) {
    return {
      isDockOpen: false,
      openConversations: [],
      recentConversations: [],
      totalUnread: 0,
      typingUsers: [],
      onlineUsers: new Set(),
      loading: false,
      isSocketConnected: false,

      setIsDockOpen: () => {},
      openConversation: () => {},
      closeConversation: () => {},
      toggleMinimizeConversation: () => {},
      sendMessage: () => Promise.reject(new Error('Non authentifié')),
      markConversationAsRead: () => {},
      searchUsers: () => Promise.resolve([]),
      createConversation: () => Promise.reject(new Error('Non authentifié')),
      loadRecentConversations: () => {},
      loadConversationDetails: () => Promise.resolve(null),
      loadMoreMessages: () => Promise.resolve(null),
      forceReloadConversations: () => {},
      deleteConversation: () => Promise.reject(new Error('Non authentifié')),
      deleteMessage: () => Promise.reject(new Error('Non authentifié')),
    }
  }

  return {
    isDockOpen: sharedState.isDockOpen,
    openConversations: sharedState.openConversations,
    recentConversations: sharedState.recentConversations,
    totalUnread: sharedState.totalUnread,
    typingUsers: sharedState.typingUsers,
    onlineUsers: sharedState.onlineUsers,
    loading: sharedState.loading,
    isSocketConnected: sharedState.isSocketConnected,

    setIsDockOpen,
    openConversation,
    closeConversation,
    toggleMinimizeConversation,
    sendMessage,
    markConversationAsRead,
    searchUsers,
    createConversation,
    loadRecentConversations,
    loadConversationDetails,
    loadMoreMessages,
    forceReloadConversations,
    deleteConversation,
    deleteMessage,
  }
}
